import styled from 'styled-components';

import ExternalAttachment from 'components/shared/ExternalAttachment';
import { withoutPortalPageCopies } from 'lib/portalPageCopy';
import Workflow, { ATTACHMENTS_VALUE_NAME } from 'models/Workflow';
import { WorkflowAction } from 'models/WorkflowAction';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

import { packetAttachment } from '../packetAttachment';

import TaskSection from './TaskSection';

export interface ResolvedDocument {
  id: string;
  filename: string | null;
  docType?: string | null;
}

// Staged beats committed, and a staged `[]` is an empty packet rather than a fall-through:
// the section describes what THIS capture produced. Never the seeded defaults — the api
// refuses to default the packet, so there is nothing there to read.
export function resolveDocuments(workflow: Workflow, retrieval: WorkflowAction | undefined): ResolvedDocument[] {
  const staged = retrieval?.outputFor(ATTACHMENTS_VALUE_NAME);
  if (staged !== undefined) {
    return Array.isArray(staged)
      ? staged.filter((entry): entry is ResolvedDocument => !!entry && typeof entry.id === 'string')
      : [];
  }
  return workflow.attachments.map(({ id, filename, docType }) => ({ id, filename, docType }));
}

interface Props {
  workflow: Workflow;
  retrieval: WorkflowAction | undefined;
  portalLabel: string;
}

export default function DocumentsSection({ workflow, retrieval, portalLabel }: Props) {
  // The extension's page copy isn't one of the retrieved documents a person confirms
  // (OLIO-3591). Staged entries carry only {id, filename}, so it's also matched by id.
  const documents = withoutPortalPageCopies(resolveDocuments(workflow, retrieval), workflow.attachments);

  return (
    <TaskSection
      title='Documents'
      complete={documents.length > 0}
      description={`Documents retrieved from ${portalLabel}. Confirm the packet is complete before creating the referral.`}
      testId='task-section-documents'>
      {documents.length === 0 ? (
        <Missing data-testid='task-documents-empty'>No documents were retrieved from {portalLabel}.</Missing>
      ) : (
        <>
          <Count data-testid='task-documents-count'>
            {documents.length} {documents.length === 1 ? 'document' : 'documents'}
          </Count>
          <Files data-testid='task-documents'>
            {documents.map((document) => (
              <ExternalAttachment
                key={document.id}
                attachment={packetAttachment(document)}
                attachableModel='Workflow'
                canModifyDocumentType={false}
              />
            ))}
          </Files>
        </>
      )}
    </TaskSection>
  );
}

const Count = styled(BodySmall)`
  color: ${colors.black75};
`;

const Files = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Missing = styled(BodySmall)`
  color: ${colors.black50};
  font-style: italic;
`;
